import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react"
import { AppData } from "../../App/AppData"
import { GetShip, GetShipContext, GetTrip } from "../../App/Context"
import { AppViewData } from "../App/Context"
import { IAnchorCircle, IAuditLogEdit, ILog, IStatus, ITrack } from "../../Types"
import { deps, many, useDataLiveEvent, useDataStore, useDeps, useExtras, useProvisionalLogs, type DepsIds } from "../../Data"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import { DayDataContext, DayDataViewData } from "./DataContext"
import { DayEventsResult, useDayEvents } from "./Events"
import { createStoreDayWriters } from "./writers"
import type { DayWriters } from "../../App/Context"
import type { DayDataProps } from "./DayView"

// A stable empty provisional list for non-current days, so `common.provisional` keeps a
// referentially-stable identity (avoids a needless `useDayDataValue` re-memo per render).
const NO_PROVISIONAL: readonly ILog[] = [];

// The centralized-store slices DayView reads. The `logs` relationship opts into the
// two calc folds so the server's normalized StoredCalculation rows stitch back onto
// `.calculated`/`.calculatedV2` (retiring the legacy `dataWithCalculations` merge).
// Ownership scoping (`dayId`) is derived from the `Day -> {Log,Status,Track}` schema
// edge, replacing the legacy hand-coded `log.dayId == day.id` filters. `tracks` reads
// from the store the same way, backed by the connection layer's `TrackApi.tracks.day`
// fetch (which also carries the day's `anchorCircles` as non-entity extras).
export const DayViewDeps = deps(SchemaEntities.Day, {
  logs: many(SchemaEntities.Log, { with: ["calculated", "calculatedV2"] }),
  statuses: many(SchemaEntities.Status),
  tracks: many(SchemaEntities.Track),
});

// The acquisition state both branches (active store-reader / idle empty-slice) need to
// assemble the shared DayDataViewData -- everything except the entity slices themselves.
interface DayDataCommon {
  events: DayEventsResult;
  // The day's in-progress provisional auto-logs, read from the centralized
  // `ProvisionalLog` store (empty for a non-current day). Threaded through both branches
  // so `useDayDataValue` folds them into the timeline the same way the legacy variant
  // folds its view-local `provisionalLogs`.
  provisional: readonly ILog[];
  details: boolean;
  setDetails: (value: boolean) => void;
  selected: boolean;
  toggleSelected: () => void;
  selectedCallback: (value?: string) => Promise<void>;
  writers: DayWriters;
}

// The resolved entity slices feeding the presentational value.
interface DayDataSlice {
  logs: ILog[] | undefined;
  statuses: IStatus[] | undefined;
  tracks: ITrack[] | undefined;
  anchorCircles: IAnchorCircle[] | undefined;
}

interface LoadedDayData {
  shipId: number;
  dayId: number;
  slice: DayDataSlice;
}

const EMPTY_DAY_SLICE: DayDataSlice = {
  logs: undefined, statuses: undefined, tracks: undefined, anchorCircles: undefined,
};

// Build the presentational DayDataViewData from a resolved slice plus the shared
// acquisition state. Identical for both branches, so DayViewUI stays impl-agnostic.
function useDayDataValue(slice: DayDataSlice, common: DayDataCommon): DayDataViewData {
  const { events } = common;
  const timelineLogs = useMemo(
    () => AppData.mergeProvisional(slice.logs ?? [], [...common.provisional]),
    [slice.logs, common.provisional],
  );
  return {
    logs: slice.logs,
    statuses: slice.statuses,
    tracks: slice.tracks,
    anchorCircles: slice.anchorCircles,
    auditLogEdits: events.auditLogEdits,
    timelineLogs,
    recordAuditLogEdits: events.recordAuditLogEdits,
    savedDay: events.savedDay,
    recordSavedDay: events.recordSavedDay,
    details: common.details,
    setDetails: common.setDetails,
    selected: common.selected,
    toggleSelected: common.toggleSelected,
    highlight: events.highlight,
    clearHighlight: events.clearHighlight,
    selectedCallback: common.selectedCallback,
    writers: common.writers,
  };
}

// Active branch -- mounted only for a selected/current day. Reads the day's slices
// from the centralized store via `useDeps` (the owner-FK `dayId` filter does the
// ownership scoping) and its Log bundle's non-entity extras (`auditLogEdits`) via
// `useExtras`, pushing the latter into the shared events store the way the legacy
// `loadLogs` effect did. `anchorCircles` ride the Track bundle's extras, read via
// `useExtras(store, "Track", ids)` the same way `auditLogEdits` ride the Log bundle.
function DayDataActive({ shipId, dayId, setAuditLogEdits, onData }: {
  shipId: number;
  dayId: number;
  setAuditLogEdits: DayEventsResult["setAuditLogEdits"];
  onData: (data: LoadedDayData) => void;
}) {
  const store = useDataStore();
  // Annotated with the declaration's own id contract, not `BoundIds`: the wide
  // `Record<string, number | undefined>` would accept any key, and a bare `const ids =
  // {...}` loses the excess-property check once it is passed on as a variable.
  const ids: DepsIds<typeof DayViewDeps.relationships> = { shipId, dayId };
  const { logs, statuses, tracks } = useDeps(DayViewDeps, ids);
  const logExtras = useExtras(store, SchemaEntities.Log, ids);
  const trackExtras = useExtras(store, SchemaEntities.Track, ids);

  useEffect(() => {
    const edits = logExtras.auditLogEdits as IAuditLogEdit[] | undefined;
    if (edits) {
      setAuditLogEdits(edits);
    }
    // `logExtras` is referentially stable (a shared empty bag until a fetch replaces
    // it), so this runs once per fetch rather than every render.
  }, [logExtras, setAuditLogEdits]);

  // `useDeps` returns readonly snapshots; the view-data contract (and its child prop
  // types) are mutable arrays. Cast without copying to preserve referential stability
  // -- consumers never mutate them.
  useLayoutEffect(() => {
    onData({
      shipId, dayId,
      slice: {
        logs: logs as ILog[],
        statuses: statuses as IStatus[],
        tracks: tracks as ITrack[],
        anchorCircles: trackExtras.anchorCircles as IAnchorCircle[] | undefined,
      },
    });
  }, [shipId, dayId, logs, statuses, tracks, trackExtras.anchorCircles, onData]);
  return null;
}

// DayView's data-acquisition half. Owns the fetch-gating UI state (`details`/`selected`)
// and reuses the shared view-local `useDayEvents`. Live-event metadata used for the
// new-committed-log highlight comes from the store's retained Log subscription, never a
// second component-owned socket subscription. Gates the store reads by conditionally
// mounting the consumer (`useDeps`
// lives in `DayDataActive`) only for an active day -- never by passing undefined ids,
// which the Log fetcher rejects.
export function DayDataView({ day, focus, initDetails, initHighlight, initSelected, isCurrentDay, children }: DayDataProps & { children?: ReactNode }) {
  const store = useDataStore();
  const [ship, trip] = [GetShip(), GetTrip()];
  const { apiUser } = GetShipContext();
  const [details, setDetails] = useState<boolean>((!day.id && !isCurrentDay) || initDetails || false);
  const [selected, setSelected] = useState<boolean>(isCurrentDay || initSelected || false);
  const [loaded, setLoaded] = useState<LoadedDayData>();
  const isCurrentTrip = trip.trip.completedAt == null;
  const focusEditable = focus && AppViewData.availableCurrentOrAll(apiUser, ship.id, isCurrentTrip);

  const events = useDayEvents({ isCurrentDay: isCurrentDay || false, focusEditable, initHighlight });

  // Provisional auto-logs live in the centralized `ProvisionalLog` store (retaining its
  // single live `Log/Provisional` subscription), replacing the legacy view-local slice.
  // They belong to whichever day is current, so a non-current day folds in none. The
  // `provisionalLogs` prop (the legacy variant's seed of markers that armed before mount,
  // #1218) is deliberately unused here: `FocusDataView` seeds the store from the very
  // same current-day response, so the store is already the single source for both.
  const storeProvisional = useProvisionalLogs(store);
  const provisional = isCurrentDay ? storeProvisional : NO_PROVISIONAL;

  const onLiveLog = useCallback((log: ILog, info: string) => {
    if (log.dayId == day.id) {
      events.notifyNewLog(log, info);
    }
  }, [day.id, events.notifyNewLog]);
  useDataLiveEvent(store, SchemaEntities.Log, onLiveLog);

  const toggleSelected = useCallback(() => setSelected((current) => !current), []);
  // Tracks load declaratively via `useDeps` once the day is active, so there's no
  // lazy per-tab track load to trigger (unlike the legacy variant's `loadTrack`).
  const selectedCallback = useCallback((_value?: string): Promise<void> => Promise.resolve(), []);

  // New days need createDay even before their acquisition consumer is mounted.
  const writers = useMemo(
    () => createStoreDayWriters(store, { shipId: ship.id, dayId: day.id, tripId: day.tripId }),
    [store, ship.id, day.id, day.tripId],
  );

  const common: DayDataCommon = { events, provisional, details, setDetails, selected, toggleSelected, selectedCallback, writers };
  const active = Boolean(day.id) && (selected || Boolean(isCurrentDay));
  const slice = active && loaded?.shipId === ship.id && loaded.dayId === day.id
    ? loaded.slice : EMPTY_DAY_SLICE;
  const value = useDayDataValue(slice, common);

  // Only the reader is conditional: creating a day must not remount its live editor.
  return (
    <DayDataContext.Provider value={value}>
      {active && <DayDataActive shipId={ship.id} dayId={day.id} setAuditLogEdits={events.setAuditLogEdits} onData={setLoaded} />}
      {children}
    </DayDataContext.Provider>
  );
}
